import { BookingStepValue } from "@/constants/Booking";
import { useAppSelector } from "@/store/hooks";
import {
  selectProviderType,
  useBookingDispatch,
} from "@/store/reducers/booking";
import { shouldShowSessionTypeStep } from "@/utils/booking.util";

interface UseBookingNavigationProps {
  currentStep: BookingStep;
}

interface UseBookingNavigation {
  onBackwardNavigation: () => void;
  onForwardNavigation: () => void;
  onOptionalAttendeesNavigation: () => void;
}

interface NavigationContext {
  currentStep: BookingStep;
  providerType?: BookingProviderType;
}

const OrderedSteps: BookingStep[] = [
  BookingStepValue.patient,
  BookingStepValue.provider,
  BookingStepValue.sessionType,
  BookingStepValue.attendees,
  BookingStepValue.duration,
  BookingStepValue.recurrence,
  BookingStepValue.availability,
  BookingStepValue.confirmation,
];

const shouldShowSessionType = (context: NavigationContext): boolean => {
  return shouldShowSessionTypeStep({
    selectedProvider: context.providerType,
  });
};

const getDefaultForwardStep = (context: NavigationContext): BookingStep => {
  const currentStepIndex = OrderedSteps.indexOf(context.currentStep);
  return OrderedSteps[currentStepIndex + 1];
};

const getDefaultBackwardStep = (context: NavigationContext): BookingStep => {
  const currentStepIndex = OrderedSteps.indexOf(context.currentStep);
  return OrderedSteps[currentStepIndex - 1];
};

const getNextStepFromProvider = (context: NavigationContext): BookingStep => {
  const shouldShowSession = shouldShowSessionType(context);
  return shouldShowSession
    ? BookingStepValue.sessionType
    : BookingStepValue.attendees;
};

const getPreviousStepFromAttendees = (
  context: NavigationContext,
): BookingStep => {
  const shouldShowSession = shouldShowSessionType(context);

  return shouldShowSession
    ? BookingStepValue.sessionType
    : BookingStepValue.provider;
};

const getForwardNavigationStep = (context: NavigationContext): BookingStep => {
  // For regular booking flow, handle special cases
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (context.currentStep) {
    case BookingStepValue.optionalAttendees:
      return BookingStepValue.duration;
    case BookingStepValue.provider:
      return getNextStepFromProvider(context);
    default:
      return getDefaultForwardStep(context);
  }
};

const getBackwardNavigationStep = (context: NavigationContext): BookingStep => {
  // For regular booking flow, handle special cases
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (context.currentStep) {
    case BookingStepValue.attendees:
      return getPreviousStepFromAttendees(context);

    case BookingStepValue.optionalAttendees:
      return BookingStepValue.attendees;

    default:
      return getDefaultBackwardStep(context);
  }
};

// TODO: PT-1446
// Add out of bounds checks for navigation
// returning `isFirstStep` and `isLastStep` booleans to allow for conditional
// rendering of navigation buttons
//
// Tests:
// navigating backwards from the first step
// navigating forwards from the last step
// add tests for `isFirstStep` and `isLastStep` booleans

const useBookingNavigation = ({
  currentStep,
}: UseBookingNavigationProps): UseBookingNavigation => {
  const { setNextStep } = useBookingDispatch();
  const providerType = useAppSelector(selectProviderType);

  const navigationContext: NavigationContext = {
    currentStep,
    providerType,
  };

  const onForwardNavigation = (): void => {
    const nextStep = getForwardNavigationStep(navigationContext);
    setNextStep(nextStep);
  };

  const onBackwardNavigation = (): void => {
    const previousStep = getBackwardNavigationStep(navigationContext);
    setNextStep(previousStep);
  };

  const onOptionalAttendeesNavigation = (): void => {
    setNextStep(BookingStepValue.optionalAttendees);
  };

  return {
    onBackwardNavigation,
    onForwardNavigation,
    onOptionalAttendeesNavigation,
  };
};

export type { UseBookingNavigation };
export default useBookingNavigation;
